import {defineMessages} from 'react-intl';
import {filter, take} from 'rxjs/operators';
import {useEffect} from 'react';

import {Conversation} from '@stripe-internal/conversations-client';

const messages = defineMessages({
  chatWithAgentEnded: {
    id: 'conversations_assistant.conversationLog.chatWithAgentEndedMessage',
    description:
      'Message to show to prompt user to close chat widget to start new chat',
    defaultMessage:
      '{name} has left the chat. Ask a new question by typing below.',
  },
  conversationEnded: {
    id: 'conversations_assistant.conversationLog.conversationEnded',
    description:
      'Message to show to prompt user to close the assistant to start new chat',
    defaultMessage:
      'You can now close the conversation or ask a new question by typing below.',
  },
  racEnded: {
    id: 'conversations_assistant.conversationLog.racEnded',
    description:
      'Message to show to prompt user to close chat widget to start new chat',
    defaultMessage:
      'Call completed. You can now close this drawer or ask a new question by typing below.',
  },
  racCanceled: {
    id: 'conversations_assistant.conversationLog.racCanceled',
    description:
      'Message to show to prompt user to close chat widget to start new chat due to cancellation',
    defaultMessage:
      'Call canceled. You can now close this drawer or ask Assistant a new question by typing below.',
  },
});

function getAgentName(conversation: Conversation): string | undefined {
  return Object.values(conversation.participants).find(
    (p) => p.role === 'agent',
  )?.displayName;
}

function getMessageForConversation(conversation: Conversation) {
  const {channel} = conversation.context;
  const agentName = getAgentName(conversation);

  if (channel === 'request_a_call') {
    if (conversation.context.requestACallDetails?.callCanceled) {
      return {
        message: messages.racCanceled,
      };
    }
    return {
      message: messages.racEnded,
    };
  } else if (agentName && channel === 'chat') {
    return {message: messages.chatWithAgentEnded, values: {name: agentName}};
  }

  return {
    message: messages.conversationEnded,
  };
}

export function useConversationLogEvents(conversation: Conversation) {
  useEffect(() => {
    const sub = conversation
      .eventsObservable()
      .pipe(
        filter((e) => e.name === 'conversation-ended'),
        take(1),
      )
      .subscribe(() => {
        // For dot_com audience, let UHLiveChannelsClient handle when to show the chat ended message.
        // It'll show after animations are complete.
        if (conversation.context.audience === 'dot_com') {
          return;
        }

        const {message, values} = getMessageForConversation(conversation);
        if (!conversation.context.supressChatEndedNotice) {
          conversation.log.appendMetaEvent({
            id: 'chat-ended-notice',
            message,
            values,
          });
        }
      });

    return () => sub.unsubscribe();
  }, [conversation]);
}
